import * as constants from './constants';
import { EarningRecord } from './earning-record';
import { Money } from './money';

/**
 * Given a string which we know to be a number containing possibly a leading
 * dollar sign and internal commas, convert to an actual Money.
 */
export function dollarStringToMoney(dollarString: string): Money {
  // Similar idea here, for the year 1965, the SSA site will display
  // 'Medicare Began in 1966' if the data is unavailable. Record this as
  // a 0 sentinel.
  if (dollarString === 'MedicareBeganIn1966') return Money.from(0);
  if (dollarString === '') return Money.from(0);
  const numberString = dollarString.replace(/[$,]/g, '');
  const value = parseInt(numberString, 10);
  if (Number.isNaN(value)) return Money.from(0);
  if (value < 0) return Money.from(0);
  return Money.from(value);
}

/**
 * Identifies the table format used in PDF Social Security statements.
 * See: https://github.com/Gregable/social-security-tools/issues/214
 */
function isPdfPaste(lines: string[]): boolean {
  const lineMatch = /[12][0-9]{3}-[12][0-9]{3}/g;
  for (let i = 0; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    // Looking for a line like "1991 - 2000":
    if (columns.length === 3 && lineMatch.test(columns[0])) {
      const years: Array<string> = columns[0].split('-');
      const startYear = parseInt(years[0], 10);
      const endYear = parseInt(years[1], 10);
      if (
        !Number.isNaN(startYear) &&
        !Number.isNaN(endYear) &&
        startYear >= 1966 &&
        endYear >= 1966 &&
        startYear <= constants.CURRENT_YEAR &&
        endYear <= constants.CURRENT_YEAR
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Parses the table format used in PDF Social Security statements.
 * See: https://github.com/Gregable/social-security-tools/issues/214
 */
function parseSsaPdfTable(lines: string[]): Array<EarningRecord> {
  const lineMatch = /[12][0-9]{3}-[12][0-9]{3}/;
  const earningsRecords: Array<EarningRecord> = [];
  let i = 0;
  for (; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');

    if (columns.length === 3 && lineMatch.test(columns[0])) {
      break;
    }
  }

  for (; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    if (columns.length !== 3 || !lineMatch.test(columns[0])) break;

    // Col 1: '1991-2000'
    // Note: years[1] is guaranteed to exist because lineMatch regex requires
    // the YYYY-YYYY format. The regex test on line 71 ensures columns[0]
    // contains a dash with valid years on both sides before we reach here.
    const years: Array<string> = columns[0].split('-');
    const startYear = parseInt(years[0], 10);
    const endYear = parseInt(years[1], 10);
    if (
      Number.isNaN(startYear) ||
      Number.isNaN(endYear) ||
      startYear < 1966 ||
      endYear < 1966 ||
      startYear > constants.CURRENT_YEAR ||
      endYear > constants.CURRENT_YEAR
    )
      return earningsRecords;
    const numYears = endYear - startYear + 1;

    // Col 2: $5,000
    const taxedEarnings = dollarStringToMoney(columns[1]).div(numYears);

    // Line 3: $5,000
    const taxedMedicareEarnings = dollarStringToMoney(columns[2]).div(numYears);

    for (let j = startYear; j <= endYear; ++j) {
      const record = new EarningRecord({
        year: j,
        taxedEarnings: taxedEarnings,
        taxedMedicareEarnings: taxedMedicareEarnings,
      });
      earningsRecords.push(record);
    }
  }

  for (; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    if (columns.length !== 3) break;

    // Col 1: "2006"
    const year = parseInt(columns[0], 10);
    if (Number.isNaN(year) || year < 1966 || year > constants.CURRENT_YEAR)
      break;

    // Co. 2: $5,000
    const taxedEarnings = dollarStringToMoney(columns[1]);

    // Col 3: $5,000
    const taxedMedicareEarnings = dollarStringToMoney(columns[2]);

    const record = new EarningRecord({
      year: year,
      taxedEarnings: taxedEarnings,
      taxedMedicareEarnings: taxedMedicareEarnings,
    });
    earningsRecords.push(record);
  }

  return earningsRecords;
}

/**
 * Parses the table format used by the SSA.gov website.
 */
function parseSsaGovTable(lines: string[]): Array<EarningRecord> {
  const earningsRecords: Array<EarningRecord> = [];
  for (let i = 0; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    // Safe to access columns[0] and columns[1]: parsePaste() pre-filters lines
    // to ensure they start with a valid year and have at least 2 columns.
    if (columns[1] === 'NotYetRecorded') {
      const record = new EarningRecord({
        year: parseInt(columns[0], 10),
        taxedEarnings: Money.from(0),
        taxedMedicareEarnings: Money.from(0),
      });
      record.incomplete = true;
      earningsRecords.push(record);
    } else {
      const record = new EarningRecord({
        year: parseInt(columns[0], 10),
        taxedEarnings: dollarStringToMoney(columns[1]),
        taxedMedicareEarnings:
          columns.length > 2 ? dollarStringToMoney(columns[2]) : Money.from(0),
      });
      earningsRecords.push(record);
    }
  }
  return earningsRecords;
}

/**
 * Parses a user formatted table of earnings records.
 */
function parseFormattedTable(lines: string[]): Array<EarningRecord> {
  const earningsRecords: Array<EarningRecord> = [];
  for (let i = 0; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    // Safe to access columns[0] and columns[1]: parsePaste() pre-filters lines
    // to ensure they start with a valid year and have at least 2 columns.
    var record = new EarningRecord({
      year: parseInt(columns[0], 10),
      taxedEarnings: dollarStringToMoney(columns[1]),
      taxedMedicareEarnings: Money.from(0),
    });
    earningsRecords.push(record);
  }
  return earningsRecords;
}

/**
 * Parses a copy / paste from this site's table.
 */
function parseThisSiteTable(lines: string[]): Array<EarningRecord> {
  const earningsRecords: Array<EarningRecord> = [];
  for (let i = 0; i < lines.length; ++i) {
    const line: string = lines[i];
    const columns: Array<string> = line.split(' ');
    // This format requires at least 3 columns (year, age, earnings).
    // Skip malformed lines that don't match the expected format.
    if (columns.length < 3) continue;
    if (columns[1] === 'NotYetRecorded') {
      const record = new EarningRecord({
        year: parseInt(columns[0], 10),
        taxedEarnings: Money.from(0),
        taxedMedicareEarnings: Money.from(0),
      });
      record.incomplete = true;
      earningsRecords.push(record);
    } else {
      var record = new EarningRecord({
        year: parseInt(columns[0], 10),
        taxedEarnings: dollarStringToMoney(columns[2]),
        taxedMedicareEarnings: Money.from(0),
      });
      earningsRecords.push(record);
    }
  }
  return earningsRecords;
}

/**
 * Determine if a string might be a year value.
 */
function isYearString(maybeYearStr: string): boolean {
  // If not an int, it's not a year.
  const maybeYear: number = parseInt(maybeYearStr, 10);
  if (Number.isNaN(maybeYear)) return false;
  // parseInt will ignore trailing garbage, so "1A" will be parsed as "1".
  // We don't want this as it could lead us to extract lines that aren't
  // valid.
  if (maybeYear.toString().length !== maybeYearStr.length) return false;

  // According to http://goo.gl/2HEj1H, the oldest person alive may have been
  // born as long back as 1887. This is debated, but it works for our simple
  // validation purposes here.
  if (maybeYear < 1887) return false;

  const CURRENT_YEAR = new Date().getFullYear();
  // If the pasted data is from ssa.gov, it won't have records greater than
  // last year, because the data comes from tax returns, which isn't processed
  // until the following year. However, some users would like to manufacture
  // data to paste into the tool, so we accept any year up to 70y in the
  // future. See https://github.com/Gregable/social-security-tools/issues/130
  if (maybeYear > CURRENT_YEAR + 70) return false;

  return true;
}

export function parsePaste(paste: string): Array<EarningRecord> {
  // We first collapse whitespace on each line as
  // different browsers insert different whitespace for column
  // separation.

  // Normalize and collapse whitespace.
  let replacedStr: string = paste.replace(/[ \t]+/g, ' ');
  // Normalize and collapse newlines.
  replacedStr = replacedStr.replace(/[\r\n]+/g, '\n');
  // Some columns will include the string "Not yet recorded" which breaks
  // columns on spaces in the string. We replace these with "NotYetRecorded".
  replacedStr = replacedStr.replace(/not yet recorded+/gi, 'NotYetRecorded');
  // Similarly, for records in 1965, the Medicare column will include the
  // string "Medicare Began in 1966". We replace this with:
  // "MedicareBeganIn1966".
  replacedStr = replacedStr.replace(
    /medicare began in 1966+/gi,
    'MedicareBeganIn1966'
  );

  // Split based on newlines.
  const lines: string[] = replacedStr.split('\n');

  if (isPdfPaste(lines)) {
    const out = parseSsaPdfTable(lines);
    out.sort((a, b) => a.year - b.year);
    return out;
  }

  // All valid lines will start with a year indicator.
  const earningsLines: string[] = [];
  for (let i = 0; i < lines.length; ++i) {
    const line: string = lines[i].trim();
    const columns: string[] = line.split(' ');
    if (!isYearString(columns[0])) continue;

    // There must be at least a year and an earnings value.
    if (columns.length < 2) {
      // Some browsers use a newline for column delimiter. See if the next
      // two lines look like dollar values, else, skip this line.
      if (i + 2 >= lines.length)
        // Must have 2 more lines.
        continue;
      // Construct a synthetic line with year, dollar dollar.
      let constructedLine: string = columns[0];
      let legitThreeLineValue: boolean = true;
      for (let j = 1; j < 3; ++j) {
        const colValue: string = lines[i + j].trim();
        if (colValue === 'NotYetRecorded' || colValue[0] === '$') {
          constructedLine += ` ${colValue}`;
        } else {
          legitThreeLineValue = false;
        }
      }
      // If we found three lines, add it to the list of earnings lines
      // and advance 2 extra lines.
      if (legitThreeLineValue) {
        earningsLines.push(constructedLine);
        i += 2;
      }
    } else {
      earningsLines.push(line);
    }
  }
  if (earningsLines.length === 0) return [];

  // There are several different formats we are interested in:
  // 1) The table at ssa.gov's website.
  // 2) The table that we produce from our example data.
  // 3) Simple row format with year + earnings.
  // TODO: Add support for parsing the AnyPIA tool file format.
  //
  // We make a determination which format it is based on the first line.
  const firstLine: string = earningsLines[0];
  const firstLineColumns: string[] = firstLine.split(' ');

  let out: Array<EarningRecord> = [];
  // The SSA website format looks like:
  //   1974 $500 $500
  if (firstLineColumns.length === 3) {
    out = parseSsaGovTable(earningsLines);
  }
  // User formatted input looks like:
  //   1974 $500
  else if (firstLineColumns.length === 2) {
    out = parseFormattedTable(earningsLines);
  }
  // The table that this tool produces has lines that look like:
  //   1974 24 $500 x 5.19 = $2,595 Top 35 Value
  else if (
    firstLineColumns.length >= 7 &&
    firstLineColumns[3] === 'x' &&
    firstLineColumns[5] === '='
  ) {
    out = parseThisSiteTable(earningsLines);
  }
  // Nothing to lose at this point, let's try guessing that the first two
  // columns are year and earnings, and the rest are garbage.
  else if (firstLineColumns.length > 3) {
    out = parseFormattedTable(earningsLines);
  }

  // SSA's tables display years in reverse chronological order which makes
  // sense for display, but is not what code usually expects, so we sort
  // output so that it's in chronological order. This also lets us handle
  // errors in user formatted years or whatnot.
  if (out.length > 1) out.sort((a, b) => a.year - b.year);

  return out;
}
